import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import MoreVertRounded from "@mui/icons-material/MoreVertRounded";
import { IconButton, Stack, Typography } from "@mui/material";

import { FileActionsMenu } from "../file-actions-menu/FileActionsMenu";
import { FileActionsMenuItem } from "../file-actions-menu/FileActionsMenu.types";
import { useFileActionItems } from "../file-actions-menu/hooks/useFileActionItems";
import { formatContentType } from "../format/formatContentType";
import { formatFileSize } from "../format/formatFileSize";

export type FileListItemMenuItem = FileActionsMenuItem;

/**
 * The standard actions (onPreview, onRename, onDownload, onCreateCaseNote, onConvertToPdf, onDelete) are
 * optional because rows differ in what a file supports — a not-yet-uploaded file can only be
 * removed, a pending move only cancelled. Each one is listed in the kebab menu only when its handler
 * is supplied; `menuItems` is for row-specific actions on top of that set, not for redeclaring it.
 */
interface Props {
  fileName: string;
  contentType?: string;
  sizeBytes?: number;
  secondaryText?: string;
  leadingIcon?: ReactNode;
  primarySlot?: ReactNode;
  struckThrough?: boolean;
  onPreview?: () => void;
  onRename?: () => void;
  onDownload?: () => void;
  onCreateCaseNote?: () => void;
  onConvertToPdf?: () => void;
  onDelete?: () => void;
  menuItems?: FileActionsMenuItem[];
  menuAriaLabel?: string;
  isBusy?: boolean;
  dense?: boolean;
}

export function FileListItem({
  fileName,
  contentType,
  sizeBytes,
  secondaryText,
  leadingIcon,
  primarySlot,
  struckThrough,
  onPreview,
  onRename,
  onDownload,
  onCreateCaseNote,
  onConvertToPdf,
  onDelete,
  menuItems,
  menuAriaLabel,
  isBusy,
  dense = false,
}: Props) {
  const { t } = useTranslation();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const closeMenu = () => setMenuAnchor(null);

  const meta =
    contentType !== undefined && sizeBytes !== undefined
      ? ` (${formatContentType(contentType)}, ${formatFileSize(sizeBytes)})`
      : "";

  const menuActionItems = useFileActionItems({
    onPreview,
    onRename,
    onDownload,
    onCreateCaseNote,
    onConvertToPdf,
    onDelete,
    extraItems: menuItems,
  });
  const hasMenu = menuActionItems.length > 0;

  return (
    <Stack direction="row" sx={{ alignItems: "center", gap: 0.5, minHeight: 0, width: "100%" }}>
      {leadingIcon}
      {primarySlot ?? (
        <Stack sx={{ overflow: "hidden", flex: 1 }}>
          <Typography
            variant="body2"
            noWrap
            sx={struckThrough ? { textDecoration: "line-through", color: "text.disabled" } : undefined}
          >
            {fileName}
            {meta}
          </Typography>
          {secondaryText !== undefined && (
            <Typography variant={dense ? "caption" : "body2"} color="text.secondary" noWrap>
              {secondaryText}
            </Typography>
          )}
        </Stack>
      )}
      {hasMenu && (
        <>
          <IconButton
            aria-label={menuAriaLabel ?? t("common.fileActions.openMenu", { fileName })}
            aria-haspopup="menu"
            disabled={isBusy}
            onClick={(e) => setMenuAnchor(e.currentTarget)}
          >
            <MoreVertRounded />
          </IconButton>
          <FileActionsMenu anchorEl={menuAnchor} onClose={closeMenu} items={menuActionItems} />
        </>
      )}
    </Stack>
  );
}
